import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { ILocalization, LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { ViewLarge, Viewport, ViewSmall } from 'bernie-view';

import { PropertyAdditionalInfoViewModel, PropertyInfoViewModel } from 'src/common/models';
import { QuestionAnswersViewModel } from 'src/common/models/hotel/hotel-details-types';

import { UitkCard, UitkCardContent, UitkCardSectionSeparatorType, UitkCardTextSegment } from 'uitk-react-cards';

import { FindAnswersInput } from 'components/infosite/find-answers/find-answers-input';
import { ImportantInfo } from './internals/important-info';
import { NavigationRefs } from 'components/infosite/navigation/navigation-refs';
import { PropertyInfoSection } from 'components/infosite/policies/property-info-section/index';
import { PropertyPolicies } from './internals/property-policies';

interface PoliciesProps {
  additionalInfo?: PropertyAdditionalInfoViewModel;
  analytics?: AnalyticsStore;
  propertyInfo?: PropertyInfoViewModel;
  hrefNextPage?: string;
  l10n?: ILocalization;
  questionAnswersViewModel?: QuestionAnswersViewModel;
  usesGrid?: boolean;
}

export type LocalizedPoliciesProps = LocalizedComponentProps & PoliciesProps;

@withLocalization
export class Policies extends React.Component<LocalizedPoliciesProps> {
  public render() {
    const { additionalInfo, analytics, propertyInfo, l10n, hrefNextPage, questionAnswersViewModel } = this.props;

    return (
      <Viewport>
        <ViewSmall>
          <PropertyContent
            propertyInfo={propertyInfo}
            additionalInfo={additionalInfo}
            questionAnswersViewModel={questionAnswersViewModel}
            analytics={analytics}
            l10n={l10n}
            hrefNextPage={hrefNextPage}
            usesGrid={false}
          />
        </ViewSmall>
        <ViewLarge>
          <PropertyContent
            propertyInfo={propertyInfo}
            additionalInfo={additionalInfo}
            questionAnswersViewModel={questionAnswersViewModel}
            analytics={analytics}
            l10n={l10n}
            usesGrid={true}
          />
        </ViewLarge>
      </Viewport>
    );
  }
}

export const PropertyContent = ({
  propertyInfo,
  additionalInfo,
  questionAnswersViewModel,
  analytics,
  l10n,
  hrefNextPage,
  usesGrid
}: PoliciesProps) => {
  const policies = propertyInfo && propertyInfo.policies;
  const petsPolicy = additionalInfo && additionalInfo.petsPolicy;
  const importantInfo = propertyInfo && propertyInfo.importantInfo;
  const shouldMention = additionalInfo && additionalInfo.shouldMention;
  const propertyAKA = additionalInfo && additionalInfo.propertyAlternativeName;
  const propertyDesc = additionalInfo && additionalInfo.propertyDescriptionSection;
  const propertyAllIncl = additionalInfo && additionalInfo.allInclusiveSection;
  const awardsAndAffil = additionalInfo && additionalInfo.awardsAndAffiliationsSection;
  const propertyManager = additionalInfo && additionalInfo.propertyManagerSection;
  const propertyAmenities = additionalInfo && additionalInfo.propertyAmenitiesSection;
  const roomAmenities = additionalInfo && additionalInfo.roomAmenitiesSection;
  const propertyStructure = additionalInfo && additionalInfo.entirePlaceSection;
  const renovationsAndClosuresSection = additionalInfo && additionalInfo.renovationsAndClosuresSection;

  return (
    <>
      <FindAnswersInput questionAnswersViewModel={questionAnswersViewModel} topPadding={true} />
      <AboutThisPropertyCard
        analytics={analytics}
        propertyDesc={propertyDesc}
        propertyStructure={propertyStructure}
        propertyAllIncl={propertyAllIncl}
        awardsAndAffil={awardsAndAffil}
        propertyManager={propertyManager}
      />
      <AmenitiesCard propertyAmenities={propertyAmenities} roomAmenities={roomAmenities} analytics={analytics} usesGrid={usesGrid} />
      <PropertyPoliciesCard
        l10n={l10n}
        petsPolicy={petsPolicy}
        policies={policies}
        renovationsAndClosuresSection={renovationsAndClosuresSection}
      />
      <ImportantInfoCard
        analytics={analytics}
        importantInfo={importantInfo}
        l10n={l10n}
        shouldMention={shouldMention}
        propertyAKA={propertyAKA}
        hrefNextPage={hrefNextPage}
      />
      <FindAnswersInput questionAnswersViewModel={questionAnswersViewModel} />
    </>
  );
};

export const AboutThisPropertyCard = ({ analytics, propertyDesc, propertyStructure, propertyAllIncl, awardsAndAffil, propertyManager }) => {
  if (!propertyDesc && !propertyStructure && !propertyAllIncl && !awardsAndAffil && !propertyManager) {
    return null;
  }

  const cardClassName = [
    's-y-padding-three',
    'm-y-padding-three',
    'all-x-padding-three',
    'l-y-margin-three',
    'l-b-padding-three',
    'xl-y-margin-three',
    'xl-b-padding-three'
  ].join(' ');

  return (
    <div className="s-b-padding-three m-b-padding-three">
      <UitkCard className={cardClassName}>
        <UitkCardContent>
          <PropertyInfoSection analytics={analytics} isToggleHidden={false} propertyInfoSection={propertyDesc} movePropertyTitle={false} />
          <PropertyInfoSection analytics={analytics} propertyInfoSection={propertyStructure} subSectionTagLevel={4} />
          <PropertyInfoSection analytics={analytics} propertyInfoSection={propertyAllIncl} subSectionTagLevel={4} isToggleHidden={false} />
          <PropertyInfoSection analytics={analytics} propertyInfoSection={awardsAndAffil} subSectionTagLevel={3} />
          <PropertyInfoSection analytics={analytics} propertyInfoSection={propertyManager} subSectionTagLevel={3} />
        </UitkCardContent>
      </UitkCard>
    </div>
  );
};

export const AmenitiesCard = ({ propertyAmenities, roomAmenities, analytics, usesGrid }) => {
  if (!propertyAmenities && !roomAmenities) {
    return null;
  }

  const gridClass = usesGrid && 'uitk-grid';

  return (
    <div className="s-b-padding-three m-b-padding-three" ref={NavigationRefs.Amenities}>
      <UitkCard className="l-y-margin-three xl-y-margin-three">
        <UitkCardContent>
          {propertyAmenities && (
            <UitkCardTextSegment separator={roomAmenities && UitkCardSectionSeparatorType.BOTTOM} className={gridClass}>
              <PropertyInfoSection
                analytics={analytics}
                propertyInfoSection={propertyAmenities}
                isAmenity={true}
                movePropertyTitle={false}
              />
            </UitkCardTextSegment>
          )}
          {roomAmenities && (
            <UitkCardTextSegment className={gridClass}>
              <PropertyInfoSection analytics={analytics} propertyInfoSection={roomAmenities} isAmenity={true} movePropertyTitle={false} />
            </UitkCardTextSegment>
          )}
        </UitkCardContent>
      </UitkCard>
    </div>
  );
};

export const PropertyPoliciesCard = ({ l10n, petsPolicy, policies, renovationsAndClosuresSection }) => {
  if (!policies) {
    return null;
  }

  return (
    <div className="s-b-padding-three m-b-padding-three">
      <UitkCard className="l-y-margin-three xl-y-margin-three">
        <UitkCardContent>
          <PropertyPolicies
            l10n={l10n}
            petsPolicy={petsPolicy}
            policies={policies}
            renovationsAndClosuresSection={renovationsAndClosuresSection}
          />
        </UitkCardContent>
      </UitkCard>
    </div>
  );
};

export const ImportantInfoCard = ({ analytics, importantInfo, l10n, shouldMention, propertyAKA, hrefNextPage }) => {
  if (!importantInfo) {
    return null;
  }

  return (
    <div className="s-b-padding-three m-b-padding-three">
      <UitkCard className="l-y-margin-three xl-y-margin-three">
        <UitkCardContent>
          <ImportantInfo
            analytics={analytics}
            importantInfo={importantInfo}
            l10n={l10n}
            shouldMention={shouldMention}
            propertyAKA={propertyAKA}
            hrefNextPage={hrefNextPage}
          />
        </UitkCardContent>
      </UitkCard>
    </div>
  );
};
